import { useEffect, useMemo, useRef, useState } from "react";
import { useTransition } from "@react-spring/web";
import {
  GetMerkActivitiesParams,
  MERK_ACTIVITY_FILTER_BY_DATE,
  MERK_ACTIVITY_TYPE,
  MerkActivity,
  MerkActivityCheckingAccountMovement,
  MerkActivityCoupon,
  MerkActivityReceipt,
} from "@letsbit/malcolmlb";
import { useGetMerkActivities } from "hooks/merk/useGetMerkActivities";
import dayjs from "dayjs";
import { useIntroducingBrokerMemberMerkActivities } from "hooks/partnerApi/useIntroducingBrokerMemberMerkActivities";

export enum ACTIVITIES_SCROLL_TYPE {
  ITEM = "ITEM",
  SEPARATOR = "SEPARATOR",
}
export interface BymaActivitiesScrollSeparator {
  id: string;
  type: ACTIVITIES_SCROLL_TYPE.SEPARATOR;
  data: {
    date: string;
  };
}
export interface BymaActivitiesScrollItem {
  id: string;
  type: ACTIVITIES_SCROLL_TYPE.ITEM;
  data:
    | MerkActivityReceipt
    | MerkActivityCoupon
    | MerkActivityCheckingAccountMovement;
}
export type BymaActivitiesScrollListItem =
  | BymaActivitiesScrollItem
  | BymaActivitiesScrollSeparator;

export function useBymaActivitiesScroll<T extends HTMLElement>(
  historyCardHeight: number,
  separatorHeight: number,
  params?: GetMerkActivitiesParams,
  firstPageOnly?: boolean,
  memberUid?: string,
) {
  const listContainerRef = useRef<T | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const refMap = useMemo(() => new WeakMap(), []);
  // const infScrollRef = React.useRef<HTMLIonInfiniteScrollElement | null>(null);
  const config = {
    tension: 125,
    friction: 20,
    precision: 0.0001,
    // duration: 350,
  };

  useEffect(() => {
    if (
      listContainerRef.current &&
      listContainerRef.current.clientHeight &&
      listContainerRef.current.clientHeight > 0 &&
      !limit
    ) {
      const ratio = listContainerRef.current.clientHeight / historyCardHeight;

      setLimit(firstPageOnly ? Math.floor(ratio) - 2 : Math.floor(ratio + 2));
    }
  }, [listContainerRef, limit, historyCardHeight, firstPageOnly]);

  const { data: dataExp, ...swr } = memberUid
    ? useIntroducingBrokerMemberMerkActivities({
        ...params,
        page_token: params?.page_token,
        member_uid: memberUid,
      })
    : useGetMerkActivities({
        ...params,
        page_token: params?.page_token,
      });

  const data = dataExp?.flat() || [];

  const dataWithSeparators: BymaActivitiesScrollListItem[] = data.reduce(
    (acc, item) => {
      if (
        item.type === MERK_ACTIVITY_TYPE.RECEIPT ||
        item.type === MERK_ACTIVITY_TYPE.COUPON ||
        item.type === MERK_ACTIVITY_TYPE.CHECKING_ACCOUNT_MOVEMENT
      ) {
        const byDateParam = params?.by_date;
        if (acc.length === 0) {
          acc.push({
            id:
              (byDateParam &&
              byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                ? item.data.agreement_date
                : item.data.settlement_date) +
              "-separator-" +
              item.id,
            type: ACTIVITIES_SCROLL_TYPE.SEPARATOR,
            data: {
              date: dayjs(
                byDateParam &&
                  byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                  ? item.data.agreement_date
                  : item.data.settlement_date,
              ).format("YYYY-MM-DD"),
            },
          });
        } else {
          const previousItem = acc[acc.length - 1];
          if (previousItem.type === ACTIVITIES_SCROLL_TYPE.ITEM) {
            const isSameDay =
              previousItem &&
              dayjs(
                byDateParam &&
                  byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                  ? previousItem.data.data.agreement_date
                  : previousItem.data.data.settlement_date,
              ).isSame(
                dayjs(
                  byDateParam &&
                    byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                    ? item.data.agreement_date
                    : item.data.settlement_date,
                ),
                "day",
              );

            if (!isSameDay) {
              acc.push({
                id:
                  (byDateParam &&
                  byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                    ? item.data.agreement_date
                    : item.data.settlement_date) +
                  "-separator-" +
                  item.id,
                type: ACTIVITIES_SCROLL_TYPE.SEPARATOR,
                data: {
                  date: dayjs(
                    byDateParam &&
                      byDateParam === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                      ? item.data.agreement_date
                      : item.data.settlement_date,
                  ).format("YYYY-MM-DD"),
                },
              });
            }
          }
        }

        acc.push({
          id: item.id,
          type: ACTIVITIES_SCROLL_TYPE.ITEM,
          data: item,
        });
      }
      return acc;
    },
    [] as BymaActivitiesScrollListItem[],
  );

  const itemsTransitions = useTransition(dataWithSeparators, {
    from: { opacity: 0, x: 50, height: historyCardHeight + 8 },
    keys: (item) => item.id,
    enter: (item) => async (next) => {
      await next({
        opacity: 1,
        height:
          item.type === ACTIVITIES_SCROLL_TYPE.ITEM
            ? historyCardHeight + 8
            : separatorHeight,
        x: 0,
      });
    },
    leave: [{ x: 50, opacity: 0 }, { height: 0 }],
    config,
  });

  const forwardedRef = (item: MerkActivity) => (ref: HTMLDivElement) => {
    ref && refMap.set(item, ref);
  };

  return {
    listContainerRef,
    itemsTransitions,
    forwardedRef,
    hasMore: !dataExp || dataExp[dataExp.length - 1].length > 0,
    data,
    ...swr,
  };
}
